<template>
<v-dialog :value="value" width="850" persistent scrollable>
  <v-card>
    <AdCardTitle>
      {{ $t('answer') }}
    </AdCardTitle>

    <v-card-text v-if="answer" class="pa-5">
      <template >
        <v-row>
          <v-col cols="auto">
            <v-select
              v-model="answerType"
              :label="$t('answer-type')"
              :items="answerChoices"
              item-value="name"
              item-text="name"
            />
          </v-col>
        </v-row>
        <v-form v-model="formIsValid" @submit.prevent>
          <v-row>
            <v-col v-for="(input, i) in availableInputs" :key="i" :cols="input.cols || 6">
              <component
                v-if="editableAnswer[answerType] && editableAnswer[answerType][0]"
                :is="input.component"
                :label="$t(input.label)"
                v-bind="input.props"
                v-model="editableAnswer[answerType][0][input.model]"
              />
            </v-col>
          </v-row>
        </v-form>
      </template>
    </v-card-text>

    <v-card-actions>
      <v-spacer/>
      <AdButton
        type="cancel"
        :text="$t('cancel')"
        @click="close"
      />
      <AdButton
        type="ok"
        :text="$t('ok')"
        :disabled="!formIsValid"
        @click="save"
      />
    </v-card-actions>
  </v-card>
</v-dialog>
</template>

<script>
import singleCodeValidation from '@/rules/singleCodeValidation'
import oidValidation from '@/rules/oidValidation'
import { cloneDeep } from 'lodash'
import { VSelect } from 'vuetify/lib'

export default {
  name: 'EnableWhenAnswerConstructor',
  props: {
    // Open / close state of the dialog
    value: {
      type: Boolean,
      default: false
    },
    // the enableWhen item
    answer: {
      type: Object
    }
  },
  data () {
    return {
      answerType: null,
      defaultValues: {
        answerBoolean: { value: 'false' }
      },
      editableAnswer: {},
      formIsValid: true

    }
  },
  computed: {
    answerChoices () {
      return this.$store.getters['server/getDecorTypes']?.data?.EnableWhenConditionType?.[0]?.element || []
    },
    availableInputs () {
      return this.inputs.filter(input => input.allowFor.includes(this.answerType) || input.allowFor.some(val => val === '*'))
    },
    inputs () {
      return [
        {
          component: 'EditableSwitch',
          label: 'value',
          allowFor: ['answerBoolean'],
          model: 'value',
          cols: 3
        },
        {
          component: 'EditableDecimal',
          label: 'value',
          allowFor: ['answerDecimal', 'answerQuantity'],
          model: 'value',
          cols: 3,
          props: { required: true }
        },
        {
          component: 'EditableCount',
          label: 'value',
          allowFor: ['answerInteger'],
          model: 'value',
          cols: 3,
          props: { required: true }
        },
        {
          component: 'EditableDate',
          label: 'value',
          allowFor: ['answerDate'],
          model: 'value',
          props: { required: true }
        },
        {
          component: 'EditableDateTime',
          label: 'value',
          allowFor: ['answerDateTime'],
          model: 'value',
          props: { required: true }
        },
        {
          component: 'EditableTime',
          label: 'value',
          allowFor: ['answerTime'],
          model: 'value',
          props: { required: true, useSeconds: true }

        },
        {
          component: 'EditableString',
          label: 'value',
          allowFor: ['answerString'],
          model: 'value',
          props: { required: true }
        },
        {
          component: 'EditableString',
          label: 'unit',
          allowFor: ['answerQuantity'],
          model: 'unit',
          props: { required: true },
          cols: 3
        },
        {
          component: VSelect,
          label: 'comparator',
          allowFor: ['answerQuantity'],
          model: 'comparator',
          props: { items: this.quantityComparatorTypes, 'item-text': 'value', 'item-value': 'value', clearable: true },
          cols: 3
        },
        {
          component: 'EditableString',
          label: 'code',
          allowFor: ['answerQuantity', 'answerCoding'],
          model: 'code',
          props: { rules: singleCodeValidation, required: this.answerType === 'answerCoding' }
        },
        {
          component: 'EditableString',
          label: 'codesystem',
          allowFor: ['answerQuantity', 'answerCoding'],
          model: 'codeSystem',
          props: { rules: oidValidation() }
        },
        {
          component: 'EditableString',
          label: 'display-name',
          allowFor: ['answerCoding'],
          model: 'displayName'
        },
        {
          component: 'EditableString',
          label: 'canonicalUri',
          allowFor: ['answerQuantity', 'answerCoding'],
          model: 'canonicalUri'
        }

      ]
    },
    hasAnswer () {
      return item => this.answerChoices.some(choice => item[choice.name] != null)
    },
    operators () {
      return this.$store.getters['server/getDecorTypes']?.data?.ConditionOperatorType?.[0]?.enumeration || []
    },
    quantityComparatorTypes () {
      return this.$store.getters['server/getDecorTypes']?.data?.QuantityComparatorType?.[0]?.enumeration || []
    }
  },
  methods: {
    cleanUpEditableAnswer () {
      this.answerChoices.forEach(({ name }) => {
        if (this.answerType !== name) {
          delete this.editableAnswer[name]
        } else if (typeof this.editableAnswer[name]?.[0] === 'object' && this.editableAnswer[name][0] !== null) {
          for (const key in this.editableAnswer[name][0]) {
            if (this.editableAnswer[name][0][key] == null || this.editableAnswer[name][0][key] === '') {
              delete this.editableAnswer[name][0][key]
            }
          }
        }
      })
    },
    close () {
      this.$emit('input', false)
    },
    save () {
      this.cleanUpEditableAnswer()
      this.$emit('save', cloneDeep(this.editableAnswer))
      this.close()
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (modalIsOpen) {
        if (modalIsOpen && this.answer) {
          this.editableAnswer = cloneDeep(this.answer)

          // Select the correct answerType
          let currentAnswerType = null
          this.answerChoices.some(({ name }) => {
            if (this.answer[name]) {
              currentAnswerType = name
              return true
            }
            return false
          })
          this.answerType = currentAnswerType || this.answerChoices[0]?.name || null
        }
      }
    },
    answerType (val) {
      if (!val) return
      if (!Array.isArray(this.editableAnswer[val])) {
        this.$set(this.editableAnswer, val, [this.defaultValues[val] || {}])
      }
    }
  }
}
</script>
